import { useMemo, useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { FinanceSalesInvoiceSequencesSelectorDocument } from 'generated/resources/graphql'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import LabeledInput from 'modules/core/components/LabeledInput'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivities } from 'modules/core/lib/activities/provider'
import i18n from 'modules/core/lib/i18n'

import { mapSerialSequences, mapToSerialSequenceFragment } from './lib/mappers'
import { GroupType, SerialSequenceSelectorProps } from './types'

export const SerialSequenceSelector = (props: SerialSequenceSelectorProps) => {
  const { openActivity } = useActivities()

  const openCreateSerialSequenceActivity = (search?: string) => {
    openActivity('finance.serialSequenceCreate', {
      refetchQuery: 'SerialSequencesCollection',
      name: search,
      legalEntityId: props.queryParams.legalEntityId?.toString(),
    })
  }
  // We skip because we only want to fetch when opening the first time the selector (if we are alwaysopen then we fetch from the very beginning)
  const [skip, setSkip] = useState<boolean>(!(props.alwaysOpen || props.defaultOpen))

  const { loading, data } = useQuery(FinanceSalesInvoiceSequencesSelectorDocument, {
    variables: {
      status: props.queryParams.status,
      legalEntityId: props.queryParams.legalEntityId,
    },
    skip,
  })

  const canCreate = data?.finance._policies.financialSequencesManage.can

  const onOpen = () => {
    if (skip) setSkip(false)
  }

  const serialSequencesEntities = useMemo(
    () => mapSerialSequences(data?.finance.salesInvoiceSequences ?? []),
    [data?.finance.salesInvoiceSequences]
  )

  const selectedSerialSequences = useMemo(
    () =>
      props.value
        ? [
            {
              id: props.value.id,
              name: props.value.name + ' -> ' + props.value.humanReadablePreview,
            },
          ]
        : [],
    [props.value]
  )

  const groupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: serialSequencesEntities,
    }),
    [serialSequencesEntities]
  )

  const selectedGroupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: selectedSerialSequences ?? [],
    }),
    [selectedSerialSequences]
  )

  const handleOnSelectSingle = (selection: EntitySelectEntity | null) => {
    props.onSelect(selection ? mapToSerialSequenceFragment(selection) : null)
  }

  const firstSequence = selectedSerialSequences[0]
  const name = !!props.value && firstSequence ? firstSequence.name : undefined

  return (
    <Box
      minWidth={props.minWidth}
      width={props.width ?? props.minWidth ?? (props.children ? 'fit-content' : 'full')}
      position="relative"
    >
      <EntitySelectWrapper
        label={i18n.t('finance.serial_sequences.selector.select_sequences')}
        hideLabel
        hiddenAvatar
        loading={loading}
        onOpen={onOpen}
        entities={groupMap['all'] || []}
        selectedGroup={'all'}
        selectedGroupedEntities={selectedGroupMap['all']}
        onSelectExpandedElements={() => {}}
        expandedElements={[]}
        selectedEntities={selectedSerialSequences}
        searchPlaceholder={i18n.t('finance.serial_sequences.selector.search')}
        selectAllLabel={i18n.t('finance.serial_sequences.selector.select_all')}
        clearLabel={i18n.t('finance.serial_sequences.selector.clear')}
        selectedLabel={i18n.t('finance.serial_sequences.selector.selected')}
        notFoundSubtitle={i18n.t('finance.serial_sequences.selector.not_found.subtitle')}
        notFoundTitle={i18n.t('finance.serial_sequences.selector.not_found.title')}
        placeholder={i18n.t('finance.serial_sequences.selector.select_sequences')}
        selectedItemsCopy={i18n.t('finance.serial_sequences.selector.sequences_selected')}
        groups={[{ label: i18n.t('finance.serial_sequences.selector.groups.none'), value: 'all' }]}
        onGroupChange={() => {}}
        onSelectMultiple={() => {}}
        singleSelector
        onSelect={handleOnSelectSingle}
        minWidth={props.minWidth}
        width={props.width}
        onCreate={canCreate ? openCreateSerialSequenceActivity : undefined}
        onCreateLabel={i18n.t('finance.serial_sequences.selector.create_new_sequence')}
      >
        {!!props.children ? (
          props.children
        ) : (
          <Box flexDirection="row" width="full">
            <LabeledInput
              aria-label="serial-sequence-selector"
              readonly={props.disabled}
              error={props.error}
              mandatory={props.mandatory}
              label={props.label}
              value={props.value?.toString() ?? undefined}
            >
              <Box
                paddingX="s16"
                paddingY="s8"
                flexDirection="row"
                height="s40"
                alignItems="center"
                width="full"
                cursor={props.disabled ? 'default' : 'pointer'}
              >
                <Box flexDirection="row" width="full" gap="s6">
                  {!props.hideSearchIcon && (
                    <Icon.Medium icon="search" color={props.disabled ? 'grey600' : 'grey800'} />
                  )}
                  <Box overflowX="hidden" textAlign="left">
                    <Text.Normal ellipsis>
                      {name ??
                        props.emptyLabel ??
                        i18n.t('finance.serial_sequences.selector.empty_label')}
                    </Text.Normal>
                  </Box>
                </Box>
                <Icon.Small icon="caret-down" />
              </Box>
            </LabeledInput>
          </Box>
        )}
      </EntitySelectWrapper>
    </Box>
  )
}
